<template>
  <div class="container-fluid position-relative">
    <div class="row">

      <div class="col-12">
        <div class="table-wrapper">
          <div
            class="table-wrapper-heading"
            v-if="$can('read_all-kpi_reports')"
          >
            <h3 class="title">
              Finance report - by people and projects
                <template v-if="reports">
                  : {{ getDateInterval }}
                </template>
              <div
                v-if="isDataPageLoading"
                class="spinner-border text-primary"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>
            </h3>

            <div class="btn btn-primary" @click.prevent="clearCache">
              Clear cache
            </div>
          </div>
          <div class="col-12 p-0 position-static" v-if="reports">

            <FullscreenTabs>
              <template v-slot:tabsControls>
                <div class="d-flex">
                  <div
                    class="table-tab-item"
                    v-for="tab in tabs"
                    :key="tab.name"
                  >
                    <b-button
                      class="button text-capitalize"
                      :variant="
                        tab.name === currentTab.name
                          ? 'info'
                          : 'secondary-outline'
                      "
                      @click="setCurrentTab(tab)"
                    >
                      {{ tab.alias }}
                    </b-button>
                  </div>
                </div>
              </template>
              <template v-slot:tabsContent>
                <div id="tooltip-container">
                  <transition name="show">
                    <div class="slide-out__container" v-if="showSlideOut">
                      <button
                        type="button"
                        aria-label="Close"
                        class="close close-slideout"
                        @click="closeSlideOut()"
                      >
                        <span class="material-icons">
                          close
                        </span>
                      </button>
                      <TableWithDetails :current-project="openProject" />
                    </div>
                  </transition>
                  <div
                    v-show="currentTab.name === 'projects'"
                    class="table__theme-default"
                  >
                    <TableForPage
                      style="margin-bottom: 0"
                      :tableData="getReportsByTabsAndMonth"
                      :totalItems="getSummaryByTabsAndMonth"
                      :totalFields="getFieldsProjects"
                      :fields="getFieldsProjects"
                      :slots="getSlotsProjectsReports"
                      :perPage="30"
                      :isFullScreen="false"
                      :tableDescription="columnDescription"
                      :extra-thead-class="'projects'"
                      showTopControls
                      showTotalTable
                    >
                      <template v-slot:tableControls>
                        <div class="table-controls-list">
                          <div class="table-controls-item filter-datepicker">
                            <b-button
                              variant="light"
                              :disabled="disablePrevMonth"
                              @click.prevent="changeMonth('prev')"
                            >
                              ‹
                            </b-button>
                            <Datepicker
                              :bootstrap-styling="true"
                              :value="_toIsoStringTime(getProjectsTab.date.to)"
                              :format="config.dateFormatDatePicker"
                              :disabledDates="{
                                to: new Date(
                                  fromDateToUTC(
                                    getProjectsTab.date.from,
                                    'YYYY-MM'
                                  )
                                ),
                                from: new Date(),
                              }"
                              minimum-view="month"
                              @selected="selectMonth"
                            />
                            <b-button
                              variant="light"
                              :disabled="disableNextMonth"
                              @click.prevent="changeMonth('next')"
                            >
                              ›
                            </b-button>
                          </div>
                          <div class="table-controls-item">
                            <ButtonSwitch
                              v-model="getTabByName('projects').sinceStartMonth"
                              firstText="Month"
                              secondText="Since start"
                            />
                          </div>
                        </div>
                      </template>
                      <template v-slot:tableControlsRight>
                          <b-button
                            class="ml-auto"
                            size="sm"
                            @click.prevent="isShowAllFields = !isShowAllFields"
                            v-if="permissions.ReadAllFinancesPPReportMoney"
                          >
                            {{ isShowAllFields ? "Hide all fields" : "Show all fields" }}
                          </b-button>
                      </template>
                      <template v-slot:[`${getSummaryKey}.billableHours`]="data" class="for-test">
                        <strong>
                          {{data['item'][getSummaryKey]['billableHours']}}  ({{calcHoursToPay(data['item'][getSummaryKey])}})
                        </strong>
                      </template>
                      <template
                        v-for="(description, name) in columnDescriptionItems"
                        v-slot:[`head(${getSummaryKey}.${name})`]="data" class="for-test">
                        <span v-b-tooltip.hover.html="{ variant: 'base', container: 'tooltip-container' }" :title="description" :key="name">
                          <span></span>

                          {{ data.label }}
                        </span>
                      </template>
                      <template v-slot:row="data">
                        <b-table
                          :fields="getSubTableByPeople"
                          :items="getDataItems(data)"
                        ></b-table>
                      </template>
                      <template v-slot:projectName="data">
                        <div class="d-flex">
                          <b-button
                            class="button hp-btn d-inline-block mr-2"
                            :variant="'secondary'"
                            size="sm"
                            v-b-tooltip.hover="{ variant: 'base' }"
                            :title="'Hours and payments report'"
                            @click="getSlideOut(data)"
                          >
                            HP
                          </b-button>
                          <TableProjectItem
                            :number="getNumber(data.index)"
                            :link="getProjectDashboardKey(data.item)"
                            :project-name="data.item.projectName"
                            :project-clients="data.item.projectClients"
                          />
                        </div>

                      </template>
                    </TableForPage>
                  </div>
                  <div
                    v-show="currentTab.name === 'people'"
                    class="table__theme-default"
                  >
                    <TableForPage
                      style="margin-bottom: 0"
                      :tableData="getReportsByTabsAndMonth"
                      :totalItems="getSummaryByTabsAndMonth"
                      :totalSlots="getSlotsTotal"
                      :totalFields="getFieldsPeople"
                      :fields="getFieldsPeople"
                      :slots="getSlotsPeopleReports"
                      :perPage="30"
                      :isFullScreen="false"
                      :extra-thead-class="'people'"
                      :tableDescription="columnDescription"
                      showTopControls
                      showTotalTable
                      @update="updateTable"
                    >
                      <template v-slot:tableControls>
                        <div class="table-controls-list">
                          <div class="table-controls-item filter-datepicker">
                            <b-button
                              variant="light"
                              :disabled="disablePrevMonth"
                              @click.prevent="changeMonth('prev')"
                            >
                              ‹
                            </b-button>
                            <Datepicker
                              :bootstrap-styling="true"
                              :value="_toIsoStringTime(getPeopleTab.date.to)"
                              :format="config.dateFormatDatePicker"
                              :disabledDates="{
                                to: new Date(
                                  fromDateToUTC(
                                    getPeopleTab.date.from,
                                    'YYYY-MM'
                                  )
                                ),
                                from: new Date(),
                              }"
                              minimum-view="month"
                              @selected="selectMonth"
                            />
                            <b-button
                              variant="light"
                              :disabled="disableNextMonth"
                              @click.prevent="changeMonth('next')"
                            >
                              ›
                            </b-button>
                          </div>
                          <div class="table-controls-item">
                            <ButtonSwitch
                              v-model="getTabByName('people').sinceStartMonth"
                              firstText="Month"
                              secondText="Since start"
                            />
                          </div>
                          <div class="table-controls-item">
                            <RoleFilter :data="tabs[1].filters" onlyProdDep @selected="changeFilter" />
                          </div>
                        </div>
                      </template>
                      <template v-slot:tableControlsRight>
                        <b-button
                          class="ml-auto"
                          size="sm"
                          @click.prevent="isShowAllFields = !isShowAllFields"
                          v-if="permissions.ReadAllFinancesPPReportMoney"
                        >
                          {{ isShowAllFields ? "Hide all fields" : "Show all fields" }}
                        </b-button>
                      </template>
                      <template v-slot:userName="slotProps">
                        <div class="d-flex align-items-center">
                          <TableUserItem :user="slotProps.item" :number="getNumber(slotProps.index)" />
                        </div>

                        <!-- <div class="specialist-user-name">
                          {{ slotProps.item.userName }}
                          <span
                            v-b-tooltip.hover="{ variant: 'base' }"
                            v-if="slotProps.item.userUnregistered"
                            title="Please register user to see full report data">
                          <font-awesome-icon icon="info" style="color:lightcoral"/>
                        </span>
                        </div>
                        <div class="specialist-user-position">
                          {{ slotProps.item.userPosition }}
                        </div>-->
                      </template>
                      <template v-slot:[`${getSummaryKey}.spentHours`]="data" class="for-test">
                        <strong>
                          {{data['item'][getSummaryKey]['spentHours']}}
                        </strong>
                      </template>
                      <template v-slot:[`${getSummaryKey}.billableHours`]="data" class="for-test">
                        <strong>
                          {{data['item'][getSummaryKey]['billableHours']}} ({{calcHoursToPay(data['item'][getSummaryKey])}})
                        </strong>
                      </template>
                      <template
                        v-for="(description, name) in columnDescriptionItems"
                        v-slot:[`head(${getSummaryKey}.${name})`]="data" class="for-test">
                        <span v-b-tooltip.hover.html="{ variant: 'base' }" :title="description" :key="name">
                          {{ data.label }}
                        </span>
                      </template>
                      <template v-slot:totalFullExpenses="data" class="for-test">
                        {{data['item'][getSummaryKey]['fullExpenses']}}
                        <span v-b-tooltip.hover="{ variant: 'base' }" title='"Project related expenses" are not included'>
                          <font-awesome-icon icon="info" style="font-size: 0.7rem;"/>
                        </span>
                      </template>
                      <template v-slot:row="data">
                        <b-table
                          :fields="getSubTableByProjects"
                          :items="getDataItems(data)"
                        ></b-table>
                      </template>
                    </TableForPage>
                  </div>
                </div>
                <PPKeyIndicatorsTable :keyIndicators="getKeyIndicatorsByTabsAndMonth" :showAllFields="isShowAllFields"></PPKeyIndicatorsTable>
              </template>
            </FullscreenTabs>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RoleFilter from '@components/RoleFilter'
import TableForPage from '@tables/TableForPage'
import Moment from 'moment'
import FullscreenTabs from '@components/Fullscreen/FullscreenTabs'
import Datepicker from 'vuejs-datepicker'
import ButtonSwitch from '@forms/ButtonSwitch'
import { permissions } from '@mocks/permissions'
import PPKeyIndicatorsTable from '@components/tables/PPKeyIndicatorsTable'
import TableUserItem from '@components/tables/TableUserItem'
import TableWithDetails from '@components/tables/TableWithDetails'
import TableProjectItem from '@components/tables/TableProjectItem'

const currentMonth = Moment().format('YYYY-MM')

export default {
  name: 'FinanceReportsPeopleAndProjects',
  components: {
    Datepicker,
    RoleFilter,
    ButtonSwitch,
    TableForPage,
    FullscreenTabs,
    PPKeyIndicatorsTable,
    TableUserItem,
    TableWithDetails,
    TableProjectItem
  },
  data () {
    return {
      permissions: permissions,
      currentPage: 1,
      perPage: 15,
      showSlideOut: false,
      filters: {
        projects: {
          type: 'multiselect',
          name: 'projects',
          label: 'Select projects',
          valueField: 'name',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        },
        users: {
          type: 'multiselect',
          name: 'users',
          label: 'Select users',
          valueField: 'id',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        },
        unitManagers: {
          type: 'multiselect',
          name: 'unitManagers',
          nameItem: 'project_id',
          label: 'Select unit manager',
          valueField: 'id',
          selectLabel: 'name',
          options: [],
          value: [],
          class: 'col-md-4'
        }
      },
      reports: null,
      doneIssues: [],
      worklogsOutsource: null,
      peopleAndProjectsInfo: null,
      tabIndex: 0,
      config: {
        dateFormat: 'YYYY-MM',
        dateFormatDatePicker: 'yyyy-MM'
      },
      currentTab: {
        name: 'projects',
        alias: 'Projects',
        date: { from: null, to: currentMonth },
        sinceStartMonth: false
      },
      tabs: [
        {
          name: 'projects',
          alias: 'Projects',
          date: { from: null, to: currentMonth },
          sinceStartMonth: false
        },
        {
          name: 'people',
          alias: 'People',
          date: { from: null, to: currentMonth },
          sinceStartMonth: false,
          filters: [
            {
              display_name: 'All',
              name: 'all'
            }
          ]
        }
      ],
      openProject: null,
      isDataPageLoading: false,

      fieldsIssues: [
        {
          key: 'key',
          sortable: true
        },
        {
          key: 'value',
          label: 'Estimate time',
          sortable: true
        }
      ],
      fieldsWorklogs: [
        {
          key: 'key',
          sortable: true
        },
        {
          key: 'value',
          label: 'Spent time',
          sortable: true
        },
        {
          key: 'worklogCreatedAt',
          sortable: true
        }
      ],
      isShowAllFields: false
    }
  },
  computed: {
    ...mapGetters({
      getCurrentUserRole: 'getCurrentUserRole',
      getCurrentUserName: 'getCurrentUserName',
      getAllUsers: 'getAllUsers',
      getJiraProjects: 'projects/getJiraProjects',
      getUnitManagers: 'users/getUnitManagers'
    }),
    getPeopleTab () {
      return this.getTabByName('people')
    },
    getProjectsTab () {
      return this.getTabByName('projects')
    },
    getMainFieldsByPeople () {
      let fields = [
        {
          key: `${this.getSummaryKey}.netRate`,
          label: 'Net rate',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.grossRate`,
          label: 'Gross rate',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.fullRate`,
          label: 'Full rate',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.externalRate`,
          label: 'External rate (avg)',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.spentHours`,
          label: 'Spent hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.billableHours`,
          label: 'Billable hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.paidHours`,
          label: 'Paid hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.grossExpenses`,
          label: 'Gross Expenses',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.fullExpenses`,
          label: 'Full Expenses',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.revenue`,
          label: 'Revenue',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.grossProfitMargin`,
          label: 'Gross Profit,%',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.grossProfit`,
          label: 'Gross Profit',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.fullProfitMargin`,
          label: 'Full Profit,%',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.fullProfit`,
          label: 'Full Profit',
          sortable: true,
          extended: true,
          class: 'column-full-profit'
        }
      ]
      fields = fields.map((item) => { item.class += ' position-sticky'; return item })

      if (!this.isShowAllFields) {
        return fields.filter((item) => !item.extended)
      }

      return fields
    },
    getFieldsPeople () {
      const fields = [
        {
          key: 'userName',
          label: 'Specialist',
          sortable: true,
          class: 'w-250 position-sticky'
        },
        ...this.getMainFieldsByPeople
      ]

      if (this.getSummaryKey !== 'summary_since_start') {
        fields.push({
          key: `more`,
          label: 'More',
          class: 't-more'
        })
      }

      return fields
    },
    getSubTableByPeople () {
      return [
        {
          key: 'userName',
          label: 'Specialist',
          sortable: true,
          class: 'w-250'
        },
        ...this.getMainFieldsByPeople
      ]
    },
    getMainFieldsByProjects () {
      let fields = [
        {
          key: `${this.getSummaryKey}.spentHours`,
          label: 'Spent hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.estimation`,
          label: 'Est',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.billableHours`,
          label: 'Billable hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.paidHours`,
          label: 'Paid hours',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.grossExpenses`,
          label: 'Gross Expenses',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.fullExpenses`,
          label: 'Full Expenses',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.revenue`,
          label: 'Revenue',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.grossProfitMargin`,
          label: 'Gross Profit,%',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.grossProfit`,
          label: 'Gross Profit',
          sortable: true,
          extended: true
        },
        {
          key: `${this.getSummaryKey}.fullProfitMargin`,
          label: 'Full Profit,%',
          sortable: true
        },
        {
          key: `${this.getSummaryKey}.fullProfit`,
          label: 'Full Profit',
          sortable: true,
          extended: true,
          class: 'column-full-profit'
        }
      ]

      fields = fields.map((item) => { item.class += ' position-sticky'; return item })

      if (!this.isShowAllFields) {
        return fields.filter((item) => !item.extended)
      }

      return fields
    },
    getFieldsProjects () {
      const fields = [
        {
          key: 'projectName',
          label: 'Project',
          sortable: true,
          class: 'w-250 position-sticky'
        },
        ...this.getMainFieldsByProjects
      ]

      if (this.getSummaryKey !== 'summary_since_start') {
        fields.push({
          key: `more`,
          label: 'More',
          class: 't-more'
        })
      }

      return fields
    },
    getSubTableByProjects () {
      return [
        {
          key: 'projectName',
          label: 'Project',
          sortable: true,
          class: 'w-250'
        },
        ...this.getMainFieldsByProjects
      ]
    },
    getHeadSlots () {
      const headSlots = {}
      for (const columnName in this.columnDescriptionItems) {
        headSlots[columnName + '.head'] = {
          name: `${this.getSummaryKey}.${columnName}`,
          type: 'head'
        }
      }
      return headSlots
    },
    getSlotsPeopleReports () {
      return {
        userName: {
          name: `userName`
        },
        spentHours: {
          name: `${this.getSummaryKey}.spentHours`
        },
        billableHours: {
          name: `${this.getSummaryKey}.billableHours`
        },
        ...this.getHeadSlots
      }
    },
    getSlotsProjectsReports () {
      return {
        projectName: {
          name: 'projectName'
        },
        'billableHours': {
          name: `${this.getSummaryKey}.billableHours`
        },
        ...this.getHeadSlots
      }
    },
    getSlotsTotal () {
      return {
        totalFullExpenses: {
          name: `${this.getSummaryKey}.fullExpenses`
        }
      }
    },
    columnDescriptionItems () {
      if (this.getCurrentTab.name === 'projects') {
        return {
          spentHours: '<strong>Spent hours</strong> = "user working hours"',
          billableHours: '<strong>Billable hours</strong> = "hours should be paid by customer"',
          paidHours: '<strong>Paid hours</strong> = "hours paid by customer (we get information from invoices)"<br>PH отображаются в том месяце, в котором инвойс был оплачен - то есть PH может быть больше чем BH (так нет расхождений с P&L репортом)',
          grossExpenses: '<strong>Gross expenses</strong> = "gross rate" * "user working hours" + "project related expenses"',
          fullExpenses: '<strong>Full expenses</strong> = "full rate" * "user working hours" + "project related expenses"',
          revenue: '<strong>Revenue</strong> = "amount of payment made by customer" (деньги оплаченные в любом месяце но за текущий период) (для отчёта PL - это фактически оплаченные деньги в месяце)',
          grossProfit: '<strong>Gross profit</strong> = "revenue" - "gross expenses"',
          grossProfitMargin: '<strong>Gross profit, %</strong> = "gross profit" / "revenue"',
          fullProfit: '<strong>Full profit</strong> = "revenue" - "full expenses"',
          fullProfitMargin: '<strong>Full profit, %</strong> = "full profit" / "revenue"'
        }
      } else if (this.getCurrentTab.name === 'people') {
        return {
          netRate: '<strong>Net rate</strong> = "payout" / ("user working hours" + "vacation and sick hours")',
          grossRate: '<strong>Gross rate</strong> = "net rate" * (("user working hours" + "vacation and sick hours") / "user working hours")',
          fullRate: '<strong>Full rate</strong> = "Gross rate + "hourly overhead"<br>' +
          'where “hourly overhead” = “monthly overhead" / "monthly prod dep working hours"<br>' +
          'and where “monthly overhead” = “regular expenses (not project related), including taxes” + "non prod dep payout”',
          externalRate: '<strong>External rate</strong> = "rate paid by customer"',
          spentHours: '<strong>Spent hours</strong> = "user working hours"',
          billableHours: '<strong>Billable hours</strong> = "hours should be paid by customer"',
          paidHours: '<strong>Paid hours</strong> = "hours paid by customer (we get information from invoices)"<br>PH покрывают BH - то есть PH отображаются только если есть соответствующие BH',
          grossExpenses: '<strong>Gross expenses</strong> = "gross rate" * "user working hours"',
          fullExpenses: '<strong>Full expenses</strong> = "full rate" * "user working hours"',
          revenue: '<strong>Revenue</strong> = "amount of payment made by customer" (деньги оплаченные в любом месяце, за текущий период и отработанные (часы проходят как billable hours - закрытые часы)) (для отчёта PL - это фактически оплаченные деньги в месяце)',
          grossProfit: '<strong>Gross profit</strong> = "revenue" - "gross expenses"',
          grossProfitMargin: '<strong>Gross profit, %</strong> = "gross profit" / "revenue"',
          fullProfit: '<strong>Full profit</strong> = "revenue" - "full expenses"',
          fullProfitMargin: '<strong>Full profit, %</strong> = "full profit" / "revenue"'
        }
      }
      return []
    },
    columnDescription () {
      return Object.values(this.columnDescriptionItems)
    },
    getSummaryKey () {
      return this.getCurrentTab.sinceStartMonth
        ? 'summary_since_start'
        : 'summary'
    },
    getDateInterval () {
      const date = this.getCurrentTab.date
      return `${date.from} - ${date.to}`
    },
    getCurrentTab () {
      return this.tabs.find((tab) => tab.name === this.currentTab.name)
    },
    getReportsByTabsAndMonth () {
      const currentTab = this.getCurrentTab
      let {
        summary,
        // eslint-disable-next-line camelcase
        summary_since_start,
        ...reports
      } = this.reports[currentTab.name][currentTab.date.to]

      reports = Object.values(reports)

      if (currentTab.filters) {
        reports = this.reportFilterByRole(reports)
      }

      reports = reports.filter(
        (item) => item[this.getSummaryKey] !== undefined
      )

      return reports
    },
    getKeyIndicatorsByTabsAndMonth () {
      const currentTab = this.getCurrentTab
      let keyIndicators = this.reports['keyIndicators'][currentTab.date.to][this.getSummaryKey] ?? {}
      return keyIndicators
    },
    getSummaryByTabsAndMonth () {
      const currentTab = this.getCurrentTab

      let summary = this.reports[currentTab.name][currentTab.date.to]['summary']
      let summarySinceStart = this.reports[currentTab.name][currentTab.date.to]['summary_since_start']

      return [
        {
          key: 'Total:',
          projectName: 'Total',
          userName: 'Total',
          summary: Object.assign({}, summary, { billableHours: `${summary['billableHours']} (${this.calcHoursToPay(summary)})` }),
          summary_since_start: Object.assign({}, summarySinceStart, { billableHours: `${summarySinceStart['billableHours']} (${this.calcHoursToPay(summarySinceStart)})` })
        }
      ]
    },
    disableNextMonth () {
      return this.getCurrentTab.date.to === currentMonth
    },
    disablePrevMonth () {
      return this.getCurrentTab.date.from === this.getCurrentTab.date.to
    }
  },
  created () {
    this.getReports()
    this.setCurrentTab(this.tabs[0])
  },
  methods: {
    setCurrentTab (tab) {
      this.currentTab = tab

      this.$forceUpdate()
    },
    getDataItems (data) {
      return Object.values(data['row']['item']['items'])
    },
    changeFilter (selectedFilters) {
      this.currentTab.filters = selectedFilters
    },
    selectMonth (data) {
      this.currentTab.date.to = this._toLocalTime(data, this.config.dateFormat)
    },
    changeMonth (type) {
      const currentMonth = Moment(
        this.currentTab.date.to,
        this.config.dateFormat
      )
      let newMonth

      if (type === 'next') {
        newMonth = currentMonth.add(1, 'months').format(this.config.dateFormat)
      } else {
        newMonth = currentMonth
          .subtract(1, 'months')
          .format(this.config.dateFormat)
      }

      this.currentTab.date.to = newMonth
    },
    getReports () {
      this._toggleDataPageLoading(async () => {
        await this.$api.finances
          .getReportsPeopleAndProjects()
          .then((response) => {
            const dateFrom = Object.keys(response.data.people)[0]
            this.reports = response.data
            // eslint-disable-next-line no-return-assign
            this.tabs.forEach((item) => (item.date.from = dateFrom))
          })
      })
    },
    prepareObjectDataToTable (data, keyName, valueName) {
      let result = []

      for (let key in data) {
        result.push({ [keyName]: key, [valueName]: data[key] })
      }

      return result
    },
    reportFilterByRole (report) {
      return report.filter((item) =>
        item.userUnregistered || item.userRoles.some(
          (reportRole) => this.getCurrentTab.filters.find(filter => filter.name === reportRole || filter.name === 'all')
        )
      )
    },
    prepareDataForGetReports (fields) {
      let data = {}

      for (let key in fields) {
        const field = fields[key]
        const fieldName = field['name']
        const fieldValue = fields[key]['value']
        const value = fieldValue.map(
          (item) => item[fields[key]['selectLabel']]
        )

        data[fieldName] = value
      }

      return data
    },
    async clearCache () {
      this.$snotify.success(
        'Cache flushed successfully',
        'Success',
        this.$store.state.snotify
      )
      try {
        await this.$api.base.clearReportsPeopleAndProjectsCache()
      } catch (error) {
        this.$snotify.error(
          error.response.data.message,
          'Error',
          this.$store.state.snotify
        )
        throw error
      }
    },
    getTabByName (name) {
      return this.tabs.find((item) => item.name === name)
    },
    calcHoursToPay (item) {
      const hoursToPay = item['billableHours'] - item['paidHours']
      return +(Math.round(hoursToPay + 'e+2') + 'e-2')
    },
    getProjectDashboardKey (item) {
      return item.isCustom ? 'OUT' : item.key
    },
    updateTable (data) {
      this.currentPage = data.currentPage
      this.perPage = data.perPage
    },
    getNumber (index) {
      if (this.currentPage > 1) {
        const curPage = this.currentPage - 1
        return (this.perPage * curPage) + index
      } else {
        return index
      }
    },
    getSlideOut (data) {
      this.showSlideOut = true
      this.openProject = {
        id: data.item.projectId,
        name: data.item.projectName,
        key: data.item.key
      }
      window.scrollTo(0, 0)
    },
    closeSlideOut () {
      this.showSlideOut = false
      this.openProject = null
    }
  }
}
</script>

<style lang="scss">
.table-wrapper {
  padding-top: 20px;
  &-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 50px;
    .btn {
      cursor: pointer;
    }
  }
  h3 {
    margin-bottom: 0;
  }
}
.table {
  transition: transform 1s;
}
.block-loader {
  height: 85px;
}
.log_time_head {
  display: inline-block;
}

.form-generator_filter {
  form {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
  }
  .form-generator__items {
    width: 900px;
  }
  .form-generator__controls {
    width: 520px;
    margin-left: 15px;
  }
  .columns-description {
    display: inline-block;
    margin-bottom: 0;
  }
  .btn {
    min-height: 43px;
    line-height: 29px;
  }
}
.filter-datepicker {
  display: flex;
  .vdp-datepicker {
    width: 120px;
    text-align: center;
    .input-group {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
    }
  }
  .form-control[readonly] {
    background-color: transparent;
  }
  .form-control {
    border: none;
    text-align: center;
  }
}
.filter-switch {
  .active {
    color: #212529;
  }
  &__label {
    color: gray;
  }
}

.filters {
  margin-bottom: 30px;
  margin-top: 10px;

  &__row {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.column-act-outsource {
  width: 188px;
}
.slide-out__container {
  width: 100%;
  height: 100%;
  background: #F3F5F7;
  position: absolute;
  top: 0;
  left: 0;
  transform: translateX(0);
  z-index: 10;
}
.show-enter-active, .show-leave-active {
  transition: transform .5s;
}
.show-enter, .show-leave-to {
  transform: translateX(100%);
}
.t-more {
  width: 75px;
}
.w-250 {
  width: 250px;
}
.hp-btn {
  min-width: 35px;
}
.close.close-slideout {
  padding: 16px;
  outline: none;

  &:focus {
    outline: none;
  }

  .material-icons {
    width: 28px;
    height: 28px;
    font-size: 28px;
  }
}
</style>
